import { showOverlay, hideOverlay, setupGlobalOverlayLogic } from './ui-utils.js';
import { initAllPageComponents, showAjaxFlash } from './page-ui.js';
import { initFeedUI } from './feed-ui.js';
import { initProfileUI } from './profile-ui.js';
import { initMatchCenter } from './match-center.js';
import { initPredictionV2 } from './prediction-v2.js';
import { initMailEditors } from './mail-editor.js';

export function configureNaja(naja) {

    let paginationViewportState = null;

    /* Zachování pozice AJAX paginatoru i při změně výšky redrawovaného obsahu. */
    naja.uiHandler.addEventListener('interaction', (e) => {
        const el = e.detail.element;
        const pagination = el?.closest?.('[data-pagination-instance]');
        if (!pagination || !el?.classList?.contains('ajax')) {
            return;
        }

        paginationViewportState = {
            instance: pagination.dataset.paginationInstance || 'default',
            top: pagination.getBoundingClientRect().top,
        };
    });


    /* 1. CONFIRM DIALOGY (data-confirm="Opravdu?") */
    naja.uiHandler.addEventListener('interaction', (e) => {
        const el = e.detail.element;
        const question = el?.dataset.confirm;
        if (question && !window.confirm(question)) {
            e.preventDefault();
            e.stopImmediatePropagation();
        }
    });

    /* 2. DOUBLE SUBMIT PROTECTION (Ochrana proti dvojkliku) */
    naja.uiHandler.addEventListener('interaction', (e) => {
        const el = e.detail.element;
        if (el.dataset.najaLocked) {
            e.preventDefault();
            e.stopImmediatePropagation();
            return;
        }

        el.dataset.najaLocked = 'true';
        el.classList.add('pointer-events-none', 'opacity-70');

        const unlock = () => {
            delete el.dataset.najaLocked;
            el.classList.remove('pointer-events-none', 'opacity-70');
            naja.removeEventListener('complete', unlock);
            naja.removeEventListener('error', unlock);
        };

        naja.addEventListener('complete', unlock);
        naja.addEventListener('error', unlock);
    });

    /* 3. PROPOJENÍ S OVERLAYEM */
    naja.addEventListener('start', (e) => {
        if (e.detail.element?.hasAttribute('data-overlay')) {
            showOverlay('AJAX Start');
        }
    });

    naja.addEventListener('complete', hideOverlay);
    naja.addEventListener('error', hideOverlay);

    naja.addEventListener('complete', () => {
        if (!paginationViewportState) {
            return;
        }

        const state = paginationViewportState;
        paginationViewportState = null;

        // Srovnat viewport okamžitě ve stejném JS tasku po redrawi.
        // Žádný requestAnimationFrame: prohlížeč tak nestihne vykreslit mezistav
        // a uživatel neuvidí skok nahoru a následný návrat k paginatoru.
        const pagination = document.querySelector(`[data-pagination-instance="${CSS.escape(state.instance)}"]`);
        if (!pagination) {
            return;
        }

        const delta = pagination.getBoundingClientRect().top - state.top;
        if (Math.abs(delta) > 1) {
            window.scrollBy({ top: delta, left: 0, behavior: 'auto' });
        }
    });

    naja.addEventListener('success', (e) => {
        const payload = e.detail.payload || {};
        const flash = payload.ajaxFlash || payload.availabilityFlash;
        if (flash?.message) {
            showAjaxFlash(flash.message, flash.type || 'info');
        }

        if (payload.notificationRefresh === true) {
            window.__clasicoNotificationPolling?.refresh?.(true);
        }

        const studio = payload.homepageStudio;
        const root = document.querySelector('[data-homepage-studio-live]');
        if (studio && root) {
            const nextStatus = String(studio.status || '');
            const nextFallback = String(studio.studioFallbackState || '');
            const stateChanged = nextStatus !== String(root.dataset.homepageStudioStatus || '')
                || nextFallback !== String(root.dataset.homepageStudioFallback || '');

            if (stateChanged) {
                window.location.reload();
                return;
            }

            const homeScore = root.querySelector('[data-homepage-home-score]');
            const awayScore = root.querySelector('[data-homepage-away-score]');
            if (homeScore && ['live', 'halftime', 'extra_time', 'penalties', 'finished'].includes(nextStatus)) {
                homeScore.textContent = String(studio.homeScore ?? 0);
            }
            if (awayScore && ['live', 'halftime', 'extra_time', 'penalties', 'finished'].includes(nextStatus)) {
                awayScore.textContent = String(studio.awayScore ?? 0);
            }
        }
    });

    /* 4. REINIT PO AJAXU */
    naja.addEventListener('complete', () => {
        document.querySelectorAll('[data-v2-lineup-builder][data-v2-nomination-saving="1"]').forEach((form) => {
            delete form.dataset.v2NominationSaving;
        });
        setTimeout(() => {
            initAllPageComponents(document);
            initFeedUI(document);
            initProfileUI(document);
            initMatchCenter(naja, document);
            initPredictionV2(document);
            initMailEditors(document);
        }, 50);
    });

    /* 5. AUTOMATICKÉ NAČÍTÁNÍ SKELETONŮ (Při prvním načtení stránky) */
    naja.registerExtension({
        initialize(naja) {
            naja.addEventListener('init', () => {
                const elements = document.querySelectorAll('[data-naja-render-on-init]');
                elements.forEach(el => {
                    const url = el.getAttribute('data-href');
                    if (url) {
                        naja.makeRequest('GET', url, null, { history: false });
                    }
                });
            });
        }
    });

    /* 6. RUČNÍ SPOUŠTĚNÍ SKELETONU (Při kliknutí na měsíc/rok/filtr) */
    naja.uiHandler.addEventListener('interaction', (e) => {
        const el = e.detail.element;
        if (el?.hasAttribute('data-show-skeleton')) {
            const snippet = document.getElementById('snippet--scheduleSnippet');
            if (snippet) {
                snippet.innerHTML = `
                    <div class="animate-pulse space-y-4 w-full">
                        <div class="h-12 bg-gray-200 rounded-lg w-full mb-6"></div>
                        <div class="border border-gray-200 rounded-lg overflow-hidden">
                            <div class="h-10 bg-gray-200 w-full mb-1"></div>
                            <div class="space-y-2 p-2">
                                <div class="h-8 bg-gray-100 rounded w-full"></div>
                                <div class="h-8 bg-gray-100 rounded w-full"></div>
                                <div class="h-8 bg-gray-100 rounded w-full"></div>
                                <div class="h-8 bg-gray-100 rounded w-full"></div>
                            </div>
                        </div>
                    </div>`;
            }
        }
    });


    /* 7. LANDING INTEREST FORM
     * Úspěch, reset i validační chyby vykresluje serverový snippet.
     * Není zde žádný klientský reset, který by mohl selhat kvůli starému bundlu.
     */

    /* 8. AKTIVACE GLOBÁLNÍ LOGIKY PRO KLASICKÉ ODKAZY */

    setupGlobalOverlayLogic();

    console.log('✅ Naja nastavena (Events, Overlay, Reinit, Skeleton-AutoLoad).');
}